import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { FaRegTrashAlt } from 'react-icons/fa';
import { itemsPerPage, THEMATIC_PLANNING_ACCESS } from '../../utils/const';
import { usePermissionAny } from '../../hooks';
import HaventPermission from '../../assets/HaventPermission';
import usePageWidth from '../shared/usePageWidth';
import CustomSelect from '../CustomSelect';
import styles from './styles.module.css';

const defaultFilters = { syid: 0, idcurriculum: 0, cid: 0, page: 0 };

// многоточие ставится только там, где пропущено больше одной страницы
const pagerItems = (page, pages) => {
    if (pages <= 1) return [];
    const keep = new Set([1, pages, page - 1, page, page + 1]);
    if (page <= 4) [2, 3, 4, 5].forEach((i) => keep.add(i));
    if (page >= pages - 3) [pages - 1, pages - 2, pages - 3, pages - 4].forEach((i) => keep.add(i));
    const list = [...keep].filter((i) => i >= 1 && i <= pages).sort((a, b) => a - b);

    const items = [];
    list.forEach((value, i) => {
        if (i) {
            const gap = value - list[i - 1];
            if (gap === 2) items.push(list[i - 1] + 1);
            else if (gap > 2) items.push('gap');
        }
        items.push(value);
    });
    return items;
};

const byName = (a, b) => String(a.name).localeCompare(String(b.name), 'ru');

// «Дисциплина РПД» в поле отбора — «наименование (индекс)» (индекс идёт отдельным
// значением; если пришло только наименование — выводим его без скобок)
const disciplineOptionName = (item) => {
    const name = item.name || item.predmet || item.title || '';
    const index = item.index || item.course_index || item.cindex || '';
    return index ? `${name} (${index})` : name;
};

const Topics = ({
    curriculum = [],
    disciplines = [],
    schoolYears = [],
    filters = defaultFilters,
    list = [],
    totalCount = 0,
    isLoading,
    hasLoaded,
    loadError,

    fetchYears,
    fetchCurriculum,
    fetchDisciplines,
    fetchThematicPlanning,
    setFilters,

    history,
}) => {
    usePageWidth('pageWide');

    const { readRight } = usePermissionAny(THEMATIC_PLANNING_ACCESS);

    // за какой год/план уже загружены зависимые перечни — чтобы перезапросить их
    // при первом открытии поля после смены вышестоящего фильтра, а не при заходе в режим
    const curriculumForYear = useRef(null);
    const disciplinesForKey = useRef(null);

    // CustomSelect мемоизирован и не сравнивает setFieldValue — обработчики читают
    // актуальные фильтры из ref, а не из устаревшего замыкания
    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    useEffect(() => {
        if (!readRight) return;
        if (!schoolYears.length) fetchYears();
        if (!hasLoaded) fetchThematicPlanning(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readRight]);

    const yearOptions = useMemo(
        () => [
            { name: 'Все', value: 0 },
            ...[...schoolYears]
                .sort((a, b) => (b.syid || 0) - (a.syid || 0))
                .map((y) => ({ name: y.name, value: y.syid })),
        ],
        [schoolYears]
    );

    // curriculum/disciplines приходят из редьюсера уже с пунктом «Все» (value 0)
    const curriculumOptions = useMemo(
        () => [
            { name: 'Все', value: 0 },
            ...curriculum
                .filter((c) => c.value !== 0)
                .map((c) => ({ name: c.name || c.label, value: c.value }))
                .sort(byName),
        ],
        [curriculum]
    );

    const disciplineOptions = useMemo(
        () => [
            { name: 'Все', value: 0 },
            ...disciplines
                .filter((d) => d.value !== 0)
                .map((d) => ({ name: disciplineOptionName(d), value: d.value }))
                .sort(byName),
        ],
        [disciplines]
    );

    const applyFilters = (next) => {
        setFilters(next);
        fetchThematicPlanning(next);
    };

    const handleYearChange = (_name, value) => {
        const syid = value || 0;
        applyFilters({ syid, idcurriculum: 0, cid: 0, page: 0 });
        fetchCurriculum(syid);
        fetchDisciplines({ idcurriculum: 0, syid });
        curriculumForYear.current = syid;
        disciplinesForKey.current = `${syid}:0`;
    };

    const handleCurriculumChange = (_name, value) => {
        const { syid } = filtersRef.current;
        const idcurriculum = value || 0;
        applyFilters({ ...filtersRef.current, idcurriculum, cid: 0, page: 0 });
        fetchDisciplines({ idcurriculum, syid });
        disciplinesForKey.current = `${syid}:${idcurriculum}`;
    };

    const handleDisciplineChange = (_name, value) => {
        applyFilters({ ...filtersRef.current, cid: value || 0, page: 0 });
    };

    const handleCurriculumMenuOpen = useCallback(() => {
        if (curriculumForYear.current === filters.syid && curriculum.length > 1) return;
        fetchCurriculum(filters.syid);
        curriculumForYear.current = filters.syid;
    }, [curriculum.length, filters.syid, fetchCurriculum]);

    const handleDisciplinesMenuOpen = useCallback(() => {
        const key = `${filters.syid}:${filters.idcurriculum}`;
        if (disciplinesForKey.current === key && disciplines.length > 1) return;
        fetchDisciplines({ idcurriculum: filters.idcurriculum, syid: filters.syid });
        disciplinesForKey.current = key;
    }, [disciplines.length, filters.syid, filters.idcurriculum, fetchDisciplines]);

    const handlePageChange = (page) => {
        applyFilters({ ...filters, page });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const isFiltersDirty = filters.syid !== 0 || filters.idcurriculum !== 0 || filters.cid !== 0;

    const handleResetFilters = () => {
        if (!isFiltersDirty) return;
        applyFilters({ syid: 0, idcurriculum: 0, cid: 0, page: 0 });
    };

    const handleRowClick = (plan) => () => {
        history.push({
            pathname: `/topics/${plan.id}`,
            state: {
                topicsList: { filters },
                plan: {
                    name: plan.name,
                    course_name: plan.course_name,
                    curriculum: plan.curriculum?.map((c) => c.name).filter(Boolean).join(', '),
                    status: plan.status,
                },
            },
        });
    };

    const renderValue = (value) => (value || value === 0 ? value : <span className={styles.dash}>—</span>);

    const pagesCount = Math.ceil((totalCount || 0) / itemsPerPage);
    const currentPage = filters.page + 1;
    const fromRecord = totalCount ? filters.page * itemsPerPage + 1 : 0;
    const toRecord = Math.min((filters.page + 1) * itemsPerPage, totalCount);

    if (!readRight) return <HaventPermission />;

    return (
        <div className={styles.page}>
            <div className={styles.head}>
                <h1 className={styles.title}>Тематические планы</h1>
                <div className={styles.headRight}>
                    <button
                        type="button"
                        className={styles.resetBtn}
                        onClick={handleResetFilters}
                        disabled={!isFiltersDirty}
                    >
                        Сбросить фильтры
                        <FaRegTrashAlt size={16} />
                    </button>
                </div>
            </div>

            <div className={styles.filters}>
                <div className={styles.fYear}>
                    <CustomSelect
                        title="Год набора"
                        width="100%"
                        withoutAll
                        options={yearOptions}
                        value={filters.syid || 0}
                        setFieldValue={handleYearChange}
                        valuesName="syid"
                    />
                </div>
                <div className={styles.fPlan}>
                    <CustomSelect
                        title="Учебный план"
                        width="100%"
                        withoutAll
                        options={curriculumOptions}
                        value={filters.idcurriculum || 0}
                        setFieldValue={handleCurriculumChange}
                        valuesName="idcurriculum"
                        onMenuOpen={handleCurriculumMenuOpen}
                    />
                </div>
                <div className={styles.fDisc}>
                    <CustomSelect
                        title="Дисциплина РПД"
                        width="100%"
                        withoutAll
                        options={disciplineOptions}
                        value={filters.cid || 0}
                        setFieldValue={handleDisciplineChange}
                        valuesName="cid"
                        onMenuOpen={handleDisciplinesMenuOpen}
                    />
                </div>
            </div>

            {isLoading || (!hasLoaded && !loadError) ? (
                <div className={styles.loader}>
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                </div>
            ) : loadError ? (
                <div className={styles.error}>
                    <div className={styles.errorTitle}>Не удалось загрузить перечень</div>
                    {loadError === 403 ? 'Доступ к режиму запрещён' : 'Сервер вернул ошибку'}
                </div>
            ) : list.length === 0 ? (
                <div className={styles.empty}>
                    <div className={styles.emptyTitle}>Ничего не найдено</div>
                    Измените условия поиска или сбросьте фильтры
                </div>
            ) : (
                <>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Название</th>
                                    <th>Дисциплина РПД</th>
                                    <th>Учебный план</th>
                                    <th className={styles.colSt}>Статус</th>
                                    <th>Кафедра</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((plan) => (
                                    <tr key={plan.id} onClick={handleRowClick(plan)}>
                                        <td className={styles.strong}>{renderValue(plan.name)}</td>
                                        <td className={styles.left}>{renderValue(plan.course_name)}</td>
                                        <td className={styles.left}>
                                            {renderValue(plan.curriculum?.map((c) => c.name).filter(Boolean).join(', '))}
                                        </td>
                                        <td className={styles.colSt}>{renderValue(plan.status)}</td>
                                        <td className={styles.left}>{renderValue(plan.cathedra)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={styles.footrow}>
                        <span className={styles.total}>
                            Показано <b>{fromRecord}-{toRecord}</b> из <b>{totalCount}</b> записей
                        </span>
                        {pagesCount > 1 && (
                            <div className={styles.pager}>
                                {pagerItems(currentPage, pagesCount).map((item, ind) =>
                                    item === 'gap' ? (
                                        <span key={`gap-${ind}`} className={styles.gap}>
                                            …
                                        </span>
                                    ) : (
                                        <button
                                            key={item}
                                            type="button"
                                            className={item === currentPage ? styles.pageOn : undefined}
                                            onClick={() => handlePageChange(item - 1)}
                                        >
                                            {item}
                                        </button>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default Topics;
